/**
 * Where did this visitor come from? Captured at LANDING, used later.
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * The server read the referrer off the API request. The API request is an XHR
 * from our own page, so its referer is always `fittohire.com` — same-origin,
 * which attribution correctly discards as "not an acquisition signal". Result:
 * 0 of 1,877 bloat jobs ever recorded a referrer. Nothing was broken; the
 * information simply is not present on the request that was being inspected.
 *
 * The acquisition referrer exists for exactly one moment: the initial page
 * load, in `document.referrer`. On a SPA every later navigation is client-side,
 * so it must be captured on first paint and carried forward.
 *
 * WHAT IS CAPTURED, AND WHAT IS NOT
 * ---------------------------------
 * Host only, never the full URL. A referring URL's query string routinely
 * carries tracking tokens and occasionally an email address; the host answers
 * the only question worth asking ("did LinkedIn send anyone?") and carries
 * none of that. This mirrors the server's own rule rather than inventing a
 * second, looser one on the client.
 *
 * UTM params are kept because they survive when the referrer does not — most
 * mobile apps, and any link opened from a native client, send no referrer at
 * all. That is very likely why today's mobile visitor arrived as `(none)`.
 *
 * sessionStorage, not localStorage: this describes THIS visit. A referrer
 * remembered from a LinkedIn click three weeks ago would quietly attribute
 * every later direct visit to LinkedIn, which is worse than knowing nothing.
 */

const KEY = 'fth_landing_v1'
const MAX_HOST = 64

export interface Landing {
  referrer?: string
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
}

function hostOf(url: string): string | undefined {
  try {
    const h = new URL(url).hostname.toLowerCase().replace(/^www\./, '')
    // Same-origin is not acquisition — identical rule to the server's.
    if (!h || h.endsWith('fittohire.com') || h === 'localhost') return undefined
    return h.slice(0, MAX_HOST)
  } catch {
    return undefined
  }
}

/** Call once, as early as possible. Idempotent — the FIRST landing wins. */
export function captureLanding(): void {
  try {
    if (sessionStorage.getItem(KEY)) return // already captured this visit
    const p = new URLSearchParams(window.location.search)
    const out: Landing = {}
    const ref = hostOf(document.referrer || '')
    if (ref) out.referrer = ref
    for (const k of ['utm_source', 'utm_medium', 'utm_campaign'] as const) {
      const v = p.get(k)
      if (v) out[k] = v.slice(0, MAX_HOST)
    }
    // Store even when empty, so a later page view inside the same visit cannot
    // overwrite a genuine direct arrival with a stale same-origin referrer.
    sessionStorage.setItem(KEY, JSON.stringify(out))
  } catch {
    /* private mode / storage disabled — attribution is optional, never fatal */
  }
}

export function getLanding(): Landing {
  try {
    return JSON.parse(sessionStorage.getItem(KEY) || '{}')
  } catch {
    return {}
  }
}

/** Headers to attach to a submit so the server can record acquisition. */
export function landingHeaders(): Record<string, string> {
  const l = getLanding()
  const h: Record<string, string> = {}
  if (l.referrer) h['X-Landing-Referrer'] = l.referrer
  if (l.utm_source) h['X-Landing-UTM-Source'] = l.utm_source
  if (l.utm_medium) h['X-Landing-UTM-Medium'] = l.utm_medium
  if (l.utm_campaign) h['X-Landing-UTM-Campaign'] = l.utm_campaign
  return h
}
